/**
 * Estimated period return — pure logic, shared by every surface that shows it.
 *
 * WHAT THIS NUMBER IS
 * `navReconciliation.daily.change_since_official_pct` is the IBKR book's move since the
 * administrator's last official strike, divided by the official NAV at that strike. When
 * the strike happens to be the prior month-end, that is a rough month-to-date figure.
 * When the administrator's close is lagging (common in the first days/weeks of a month)
 * the same field spans a longer period and calling it "MTD" would assert a period the
 * number does not measure. Deciding that honestly is most of what this module does.
 *
 * WHAT IT IS NOT
 * - Not official. NAV Fund Services is the sole official source of fund performance.
 * - Not an investor return. The incentive fee carries a per-investor high-water mark and
 *   crystallizes annually, so no LP's return equals the fund-level NAV change.
 * - The HEADLINE is not net of the incentive fee (see MGMT_FEE_ANNUAL_RATE below) — but
 *   the OFFICIAL Net ROR is net of the ACCRUED incentive, so a labelled modelled-20%
 *   secondary line exists for the like-for-like read (INCENTIVE_RATE_MODEL).
 *
 * Every function here is pure and side-effect free so the period and fee rules can be
 * tested directly (navPerformance.test.ts) rather than only observed through a rendered
 * tile. No `?? 0` anywhere: a missing input yields null, never a fabricated zero.
 */

import type { NavCalibration, NavDaily, NavNetRor, NavReconciliation } from "./navTypes";

// ---------------------------------------------------------------------------
// Fee model
// ---------------------------------------------------------------------------

/**
 * Management fee rate used for the MODELED accrual on the estimated return.
 *
 * CIO-confirmed as 2%/yr (2/20 with an 8% soft hurdle and a high-water mark). The same
 * terms are coded in research at HelionBacktest/compute_net_of_fee.py:21, which carries
 * its own "confirm against the fund's PPM / governing documents" caveat — that caveat
 * applies here too, and specifically to the ACCRUAL CONVENTION below, which was not
 * supplied and is assumed.
 *
 * ASSUMPTION (needs PPM confirmation before anything investor-visible): accrued
 * straight-line on an actual/365 basis against the official NAV at the strike. The
 * executed documents may instead specify average AUM over the period, a 360-day basis,
 * monthly crystallization, or a different fee base.
 *
 * THE INCENTIVE FEE: the EXACT figure is not computable at fund level (8% soft hurdle
 * with full catch-up, per-investor high-water marks, annual crystallization), so the
 * HEADLINE stays net of MANAGEMENT fee only and is labeled that way. Since 2026-09-02 a
 * flat 20% MODEL (INCENTIVE_RATE_MODEL below) feeds the labelled co-headline —
 * "est. official net · modelled" (haircut on the RAW net in profitable months,
 * pass-through unchanged in losing months) — because the OFFICIAL Net ROR was verified to be net of
 * the administrator's monthly ACCRUED incentive reallocation; the model is approximate
 * by design (~23% effective observed in catch-up months) and never replaces the headline.
 */
export const MGMT_FEE_ANNUAL_RATE = 0.02;

/** Day-count denominator for the straight-line accrual above. */
export const MGMT_FEE_DAY_BASIS = 365;

/**
 * Fraction of the implied management fee that the administrator's reported expenses must
 * reach before they are treated as BOOKED rather than merely not-yet-posted.
 *
 * Set at half: comfortably below any real month (where the management fee alone should
 * account for the great majority of expenses) yet far above the ~0.00 that an open month
 * reports. Confirmed against live data 2026-08-11 — four business days into August the
 * administrator reported $0.00 across all four expense lines, which is not a fee-free
 * period, only an unbooked one.
 */
export const EXPENSES_BOOKED_FLOOR = 0.5;

/**
 * Bank-cash rise, as a fraction of the anchor NAV, above which the tile flags a probable
 * capital inflow. Set at 10bps: comfortably above a month of interest on the reserve,
 * comfortably below any real subscription.
 *
 * This is the ONLY flow signature monthly-tier reporting gives us, and it is a partial
 * one. A subscription that arrives and is deployed into the brokerage account within the
 * same period never moves the bank balance at all — July 2026 was precisely that, units
 * +2.98% with the bank flat, and no basis available to us detected it. Treat the absence
 * of this badge as "no inflow seen", never as "no inflow".
 */
export const FLOW_BADGE_FLOOR = 0.001;

/**
 * Headline incentive rate for the MODELLED "est. official net" co-headline
 * (CIO-approved 2026-09-02; promoted to a co-headline the same day). Deliberately
 * the stated 20%, NOT the ~23% effective rate observed in catch-up months —
 * fitting to two observed months is the exact trap the calibration exists to
 * catch. Flat and one-sided: no hurdle position, no per-investor HWM, no
 * down-month reversal credits; the HAIRCUT applies to positive months only — a
 * losing month passes through unchanged (and the official tends to land slightly
 * better than it, since accrual reversals are not modeled).
 * Context (verified against the Aug-2026 package): the OFFICIAL Net ROR is net of
 * the administrator's monthly ACCRUED INCENTIVE reallocation, so the before-carry
 * headline legitimately reads ~20-23% of profit above the official in up months.
 */
export const INCENTIVE_RATE_MODEL = 0.2;

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const isNum = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/**
 * Parse an administrator or broker date string to a LOCAL calendar date.
 * Accepts the two formats this payload actually mixes: "MM-DD-YYYY" (administrator
 * accounting dates) and "YYYY-MM-DD" (broker/SQL dates).
 *
 * Constructed via `new Date(y, m, d)` rather than `Date.parse`, because parsing
 * "2026-06-30" yields UTC midnight and can render as the 29th west of Greenwich — which
 * would silently shift a month-end strike out of its own month and flip the period label.
 */
export function parseNavDate(value?: string | null): Date | null {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  let y: number, m: number, d: number;
  const mdy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (mdy) {
    m = Number(mdy[1]);
    d = Number(mdy[2]);
    y = Number(mdy[3]);
  } else if (ymd) {
    y = Number(ymd[1]);
    m = Number(ymd[2]);
    d = Number(ymd[3]);
  } else {
    return null;
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  // Reject impossible dates that JS would roll forward (e.g. 02-31 -> Mar 3).
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** Whole days between two local dates (b - a). */
export function daysBetween(a: Date, b: Date): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / MS_PER_DAY);
}

/**
 * Last WEEKDAY of the calendar month containing `d`.
 *
 * Deliberately weekend-aware but NOT holiday-aware — the dashboard has no market
 * calendar, and importing one for this would be a heavier dependency than the decision
 * warrants. The failure mode is chosen to be safe in the honest direction: when a
 * month-end lands on an exchange holiday, the administrator's strike falls on the
 * preceding business day, this function returns the later (holiday) date, the strike
 * therefore fails the equality test, and the period is labeled LITERALLY ("since 12-30")
 * instead of "MTD". The label under-claims rather than over-claims, which is the correct
 * bias for a performance figure.
 */
export function lastWeekdayOfMonth(d: Date): Date {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  while (last.getDay() === 0 || last.getDay() === 6) {
    last.setDate(last.getDate() - 1);
  }
  return last;
}

/** True when `strike` is the final weekday of the calendar month immediately preceding
 *  the month that `asOf` falls in — the only case in which "month-to-date" is accurate. */
export function isPriorMonthEnd(strike: Date, asOf: Date): boolean {
  const priorMonth = new Date(asOf.getFullYear(), asOf.getMonth() - 1, 1);
  if (strike.getFullYear() !== priorMonth.getFullYear() || strike.getMonth() !== priorMonth.getMonth()) {
    return false;
  }
  const lastBiz = lastWeekdayOfMonth(priorMonth);
  return strike.getDate() === lastBiz.getDate();
}

// ---------------------------------------------------------------------------
// View model
// ---------------------------------------------------------------------------

export type PeriodReturnCaveat = {
  /** Short badge text shown on the tile face. */
  badge: string;
  /** Full explanation for the tooltip / methodology. */
  detail: string;
};

export type PeriodReturnView = {
  /** False when there is not enough data to make any return claim at all. */
  available: boolean;
  /** Why nothing is rendered, when available === false. */
  unavailableReason?: string;

  /** "Est. MTD return" only when the strike really is the prior month-end. */
  periodLabel: string;
  /** True when periodLabel is a genuine month-to-date claim. */
  isMonthToDate: boolean;
  /** e.g. "vs official NAV 06-30-2026". Always shown — the anchor is never implicit. */
  anchorLabel: string;

  /** Gross of any fee accrued since the strike. Decimal fraction. */
  grossPct: number | null;
  grossUsd: number | null;

  /** Net of fees. Decimal fraction. */
  netPct: number | null;
  netUsd: number | null;
  /** Total fee/expense subtracted, in dollars (always >= 0). */
  mgmtFeeAccrualUsd: number | null;
  /** Days the MODELLED portion covers. Zero when the administrator's actual expenses
   *  already cover the whole period. */
  accrualDays: number | null;
  /** Where the fee figure came from — drives the tile's label, so a modelled number is
   *  never presented as though the administrator had booked it. */
  feeBasis: "actual" | "actual+modelled_tail" | "modelled";
  /** Administrator's actual booked expenses since the strike, when available. */
  actualExpensesUsd: number | null;
  /** Last accounting day the actual expenses cover (MM-DD-YYYY). */
  expensesThroughDate: string | null;

  /** Ordered, de-duplicated caveats. Empty when the figure is clean. */
  caveats: PeriodReturnCaveat[];

  /** Capital-flow display state from the backend's operator ledger: "clean"
   *  (attested, no flows), "adjusted" (attested flows subtracted), "unattested"
   *  (no ledger row for a month in the window — the percentage IS shown, unadjusted,
   *  with a "flows unconfirmed" flag, per the CIO's always-show-a-percentage ruling
   *  2026-08-31), "suppressed" (percentage withheld — attested flows too large for
   *  the beginning-NAV denominator), or null on a backend predating the flow ledger. */
  flowState: "clean" | "adjusted" | "unattested" | "suppressed" | null;
  /** True when the PERCENTAGE is deliberately withheld — attested flows too large
   *  for the beginning-NAV denominator. Dollars remain shown: under flows the
   *  NAV level stays accurate while the percentage does not (Oct-2025: estimated NAV
   *  within 0.3% of official while the raw percentage printed +314.8% against a real
   *  +3.77%). A missing figure sends the reader to the official package; a wrong one
   *  does not. */
  percentSuppressed: boolean;
  suppressionReason: string | null;
  /** Signed net attested flows subtracted from the numerator, when adjusted. */
  flowAdjustmentUsd: number | null;
  /** Provenance of the flow figure: "manual" | "admin" | "flex" | "none". */
  flowSource: string | null;
  /** True when the DOLLARS shown are themselves flow-adjusted. False in the
   *  suppressed-unattested state, where the dollars fall back to the raw
   *  paired/broker change and may include the entire unattested flow — the tile
   *  must label them, not render them like the sound magnitude-suppressed case. */
  dollarsFlowAdjusted: boolean;
  /** The anticipated official net figure (fraction) — the co-headline "≈ est.
   *  official net · modelled" (CIO request 2026-09-02, promoted from a footnote
   *  line so the like-for-like read on the official Net ROR is discernible at a
   *  glance). Base × 0.8 when the period is positive (flat 20% incentive model —
   *  the official is net of the accrued incentive); base UNCHANGED when zero or
   *  negative (no incentive accrues on a loss; reversals of prior accruals, which
   *  would make the official read slightly BETTER than this, are one-sided and
   *  deliberately not modeled). The base is ALWAYS the raw net — never the
   *  drift-corrected figure, whose bias is measured against the carry-netted
   *  official and would double-count the incentive on a profitable streak. Null
   *  on suppressed/unattested states and legacy backends. ALWAYS rendered with a
   *  "modelled" label. */
  estOfficialNetPct: number | null;
  /** True when the 20% haircut was actually applied (positive base) — drives the
   *  co-headline tooltip phrasing; a pass-through down month explains itself
   *  differently than a haircut up month. */
  estOfficialHaircutApplied: boolean;
  /** Monthly self-calibration from the backend: last month's estimate scored against
   *  the official Net ROR the day the strike landed. Null until supplied, or when the
   *  backend could not stand behind a score. */
  calibration: NavCalibration | null;
  /** Drift-corrected net figure: netPct minus the calibration's trailing bias.
   *  Present only when the record supports a correction AND the flow state is
   *  clean/adjusted (correcting mark drift on top of an unattested-flow figure
   *  would imply precision the number does not have). The RAW netPct stays
   *  alongside — a correction is shown, never silently substituted. */
  correctedNetPct: number | null;
  driftBiasPp: number | null;
  /** Estimated NAV per share (LP-structure-neutral; flow-immune by construction).
   *  Null when the window's flows are unattested or the backend predates it. */
  estNavpsNow: number | null;
  navpsAtStrike: number | null;
  estNavpsRetPct: number | null;
  /** Fee cash added back to the numerator (paid via a subscription sweep; cost
   *  carried once by the expense accrual). Null when none. */
  feeAddBackUsd: number | null;

  /** Administrator's official net return, only when units are confirmed. */
  official: {
    available: boolean;
    /** Reason the official figure is withheld, when available === false. */
    withheldReason?: string;
    mtdPct: number | null;
    ytdPct: number | null;
    itdPct: number | null;
    asOf: string | null;
  };
};

/** Convert a raw Net ROR value to a decimal fraction using the DECLARED units. Returns
 *  null when units are unconfirmed — the scale is never guessed. */
export function netRorToFraction(value: number | null | undefined, units: NavNetRor["units"]): number | null {
  if (!isNum(value)) return null;
  if (units === "fraction") return value;
  if (units === "percent") return value / 100;
  return null;
}

const FRAMING_UNAVAILABLE = "Administrator reconciliation not loaded.";

/**
 * Build the full view model for the estimated period return.
 *
 * `recon` is the whole reconciliation block (not just `.daily`) because two honesty
 * checks live outside `.daily`: `adjusted` on the base block, and `administrator.broker_as_of`
 * which reveals whether the historical leg of the numerator actually came from the strike
 * date or from an earlier session.
 */
export function buildPeriodReturnView(recon?: NavReconciliation | null): PeriodReturnView {
  const empty: PeriodReturnView = {
    available: false,
    unavailableReason: FRAMING_UNAVAILABLE,
    periodLabel: "Est. return since official strike",
    isMonthToDate: false,
    anchorLabel: "official NAV not loaded",
    grossPct: null,
    grossUsd: null,
    netPct: null,
    netUsd: null,
    mgmtFeeAccrualUsd: null,
    accrualDays: null,
    feeBasis: "modelled",
    actualExpensesUsd: null,
    expensesThroughDate: null,
    caveats: [],
    flowState: null,
    percentSuppressed: false,
    suppressionReason: null,
    flowAdjustmentUsd: null,
    flowSource: null,
    dollarsFlowAdjusted: false,
    estOfficialNetPct: null,
    estOfficialHaircutApplied: false,
    calibration: null,
    correctedNetPct: null,
    driftBiasPp: null,
    estNavpsNow: null,
    navpsAtStrike: null,
    estNavpsRetPct: null,
    feeAddBackUsd: null,
    official: { available: false, withheldReason: FRAMING_UNAVAILABLE, mtdPct: null, ytdPct: null, itdPct: null, asOf: null },
  };

  const daily: NavDaily | null | undefined = recon?.daily;
  const admin = recon?.administrator;

  // --- Official (administrator) leg — independent of the estimate ------------
  const ror = admin?.net_ror;
  const units = ror?.units ?? null;
  const officialAvailable = !!ror && (units === "percent" || units === "fraction");
  const official: PeriodReturnView["official"] = {
    available: officialAvailable,
    withheldReason: !ror
      ? "Administrator has not reported a Net ROR for this strike."
      : officialAvailable
      ? undefined
      : "Net ROR scale not yet confirmed against a live payload (NAV_NET_ROR_UNITS unset) — withheld rather than shown at a possibly wrong scale.",
    mtdPct: officialAvailable ? netRorToFraction(ror?.mtd, units) : null,
    ytdPct: officialAvailable ? netRorToFraction(ror?.ytd, units) : null,
    itdPct: officialAvailable ? netRorToFraction(ror?.itd, units) : null,
    asOf: admin?.admin_as_of ?? null,
  };

  if (!daily) return { ...empty, official };

  const anchorNav = isNum(daily.last_official_nav) ? daily.last_official_nav : null;
  const strikeStr = daily.last_official_date ?? null;
  const strike = parseNavDate(strikeStr);

  // BASIS SELECTION, in order of trust:
  //
  //   1. FLOW-ADJUSTED (change_flow_adj_*): attested capital flows from the operator
  //      ledger (dbo.CapitalFlows) subtracted from the BROKER-ONLY change. This is what
  //      fixes the July-2026 failure (+2.25% shown vs −0.18% official) — a subscription
  //      admitted, swept and deployed inside the month read as performance. The base is
  //      deliberately broker-only, NOT paired: at a month-end strike the bank holds
  //      subscription cash received but not yet admitted, so pairing + subtracting
  //      charges the same capital twice (proof pinned in the backend's
  //      tests/test_nav_flow_adjustment.py). When the backend says "suppressed", the
  //      PERCENTAGE is withheld and dollars stay.
  //   2. PAIRED (change_incl_bank_*): cancels an admitted bank↔broker transfer. Used
  //      when the backend predates the flow ledger.
  //   3. BROKER-ONLY (change_since_official_*): last resort when the strike-date bank
  //      balance is unavailable. Against the administrator's official return this basis
  //      ran a median +1.4pp/month HIGH over the seven stabilised months Jan–Jul 2026
  //      (≈+2.7pp over all ten completed months including the 2025 ramp — an earlier
  //      version of this comment said "+137bps over ten completed months", conflating
  //      the two populations).
  const flowState: PeriodReturnView["flowState"] =
    daily.flow_state === "clean" || daily.flow_state === "adjusted" ||
    daily.flow_state === "unattested" || daily.flow_state === "suppressed"
      ? daily.flow_state
      : null;
  // Only MAGNITUDE suppression withholds the percentage now. An unattested month
  // shows the unadjusted figure WITH a flag (CIO ruling 2026-08-31: a flagged
  // percentage beats a withheld one; the flag is what keeps a July-2026 repeat from
  // printing as a confident clean number). A transition-window backend may still send
  // "suppressed" for an unattested month — that renders dollars-only for the minutes
  // until both sides are current, which errs honest.
  const percentSuppressed = flowState === "suppressed";
  const suppressionReason = percentSuppressed
    ? daily.flow_state_reason ??
      "Attested capital flows are too large for a beginning-NAV percentage — withheld."
    : null;
  const flowAdjusted =
    (flowState === "clean" || flowState === "adjusted") && isNum(daily.change_flow_adj_pct);

  const paired = isNum(daily.change_incl_bank_pct);
  const grossPct = percentSuppressed
    ? null
    : flowAdjusted
    ? (daily.change_flow_adj_pct as number)
    : paired
    ? (daily.change_incl_bank_pct as number)
    : isNum(daily.change_since_official_pct)
    ? daily.change_since_official_pct
    : null;
  const grossUsd = flowAdjusted || percentSuppressed
    ? // Suppressed still shows dollars: prefer the flow-adjusted USD (present when the
      // flow was quantified but too large), else fall back down the same ladder.
      isNum(daily.change_flow_adj_usd)
      ? daily.change_flow_adj_usd
      : paired && isNum(daily.change_incl_bank_usd)
      ? daily.change_incl_bank_usd
      : isNum(daily.change_since_official_usd)
      ? daily.change_since_official_usd
      : null
    : paired
    ? (isNum(daily.change_incl_bank_usd) ? daily.change_incl_bank_usd : null)
    : isNum(daily.change_since_official_usd)
    ? daily.change_since_official_usd
    : null;

  if ((grossPct === null && !percentSuppressed) || anchorNav === null || anchorNav <= 0 || !strike) {
    return {
      ...empty,
      official,
      unavailableReason:
        grossPct === null
          ? "No change-since-strike figure in the reconciliation feed."
          : !strike
          ? "Administrator strike date missing or unparseable — no period can be stated."
          : "No official NAV to divide by.",
    };
  }

  // Accrue to the DATA's own date, not the viewer's clock, so the figure is
  // reproducible and does not drift with the browser timezone.
  const asOf = parseNavDate(daily.ibkr_live_as_of) ?? new Date();

  const monthToDate = isPriorMonthEnd(strike, asOf);
  const periodLabel = monthToDate ? "Est. MTD return" : "Est. return since official strike";
  const anchorLabel = `vs official NAV ${strikeStr}`;

  // --- Fees -----------------------------------------------------------------
  // Prefer the administrator's ACTUAL booked expenses. Their accounting date runs a few
  // business days behind, so the uncovered tail is modelled at the management-fee rate and
  // the basis is reported honestly. Falls back to a fully modelled accrual when the feed is
  // unavailable — but never silently: `feeBasis` drives the tile's wording.
  const actual = daily.expenses_since_official;
  const reportedUsd = isNum(actual?.total_usd) ? Math.abs(actual!.total_usd as number) : null;
  const reportedThrough = reportedUsd !== null ? parseNavDate(actual?.through_date) : null;

  // BOOKED-vs-INCURRED GUARD.
  // The administrator books the management fee on its own cycle (monthly for this fund),
  // so daily rows inside an open month legitimately read 0.00: the fee has been INCURRED
  // but not yet BOOKED. Taking that at face value would report an open month as nearly
  // fee-free and overstate the return -- the exact direction of error this tile exists to
  // avoid. So reported expenses are trusted only when they are at least plausible against
  // what the management fee ALONE implies for the days they cover. Same philosophy as the
  // backend's _accrual_plausible guard: when a feed's value is implausible for the period,
  // do not publish it, fall back to the reliable alternative and say so.
  const coveredDays = reportedThrough ? Math.max(0, daysBetween(strike, reportedThrough)) : 0;
  const impliedMgmt = (anchorNav * MGMT_FEE_ANNUAL_RATE * coveredDays) / MGMT_FEE_DAY_BASIS;
  const booked =
    reportedUsd === null
      ? false
      : coveredDays === 0
      ? true // nothing to compare against; no opinion
      : reportedUsd >= EXPENSES_BOOKED_FLOOR * impliedMgmt;

  const actualUsd = booked ? reportedUsd : null;
  const actualThrough = booked ? reportedThrough : null;

  // Days still needing a model: from the last covered accounting day (or the strike, when
  // nothing is covered) up to the NAV's as-of date. Never negative.
  const modelFrom = actualThrough ?? strike;
  const rawDays = daysBetween(modelFrom, asOf);
  const accrualDays = rawDays > 0 ? rawDays : 0;
  const modelledUsd = (anchorNav * MGMT_FEE_ANNUAL_RATE * accrualDays) / MGMT_FEE_DAY_BASIS;

  const feeBasis: PeriodReturnView["feeBasis"] =
    actualUsd === null ? "modelled" : accrualDays > 0 ? "actual+modelled_tail" : "actual";

  const mgmtFeeAccrualUsd = (actualUsd ?? 0) + modelledUsd;
  const netUsd = grossUsd === null ? null : grossUsd - mgmtFeeAccrualUsd;
  // Null when the percentage is suppressed — the dollars carry the fee subtraction.
  const netPct = grossPct === null ? null : grossPct - mgmtFeeAccrualUsd / anchorNav;

  // --- Caveats ---------------------------------------------------------------
  const caveats: PeriodReturnCaveat[] = [];

  // When the flow ledger is active (flowState non-null), it supersedes the three
  // legacy flow-shaped badges below: attestation answers the question those badges
  // could only hint at, and the flow-adjusted basis is broker-only BY DESIGN, so
  // "broker leg only" would misread as a defect. The ledger's own states carry the
  // honesty instead (the "% withheld" chip, and "reserve moved?" below).
  if (percentSuppressed && suppressionReason) {
    caveats.push({ badge: "% withheld", detail: suppressionReason });
  }

  // Unattested month: the percentage renders on the unadjusted basis and this flag is
  // the ONLY thing distinguishing it from a genuinely clean month. Do not soften it.
  if (flowState === "unattested") {
    caveats.push({
      badge: "flows unconfirmed",
      detail:
        (daily.flow_state_reason ??
          "A month in this window has no flow-ledger entry.") +
        " Until the month's row exists, any subscription or redemption in the period " +
        "sits inside this figure as though it were performance (July 2026 read +2.25% " +
        "on a −0.18% month exactly this way).",
    });
  }

  // Material bank move in a month with ZERO attested flows: probably an admitted
  // reserve transfer between IBKR and the bank — the one case the broker-only base
  // misreads (by the full transfer, until the next strike). Flagged, never absorbed.
  const reserveMove = isNum(daily.unexplained_bank_move_usd)
    ? (daily.unexplained_bank_move_usd as number)
    : null;
  if (reserveMove !== null) {
    caveats.push({
      badge: "reserve moved?",
      detail:
        `Off-broker bank cash moved $${Math.abs(reserveMove).toLocaleString(undefined, { maximumFractionDigits: 0 })} ` +
        "since the strike with zero attested capital flows — most likely an admitted reserve transfer " +
        "between IBKR and the bank. This figure measures the broker leg only, so such a transfer reads " +
        "as performance (in the transfer's direction) until the administrator's next strike.",
    });
  }

  // A RISE in off-broker bank cash is the one capital-inflow signature we can observe:
  // market moves never touch the bank balance, and deploying the reserve lowers it. Sized
  // against the anchor so a routine interest credit does not badge the tile.
  const bankDelta = isNum(daily.bank_delta_usd) ? (daily.bank_delta_usd as number) : null;
  if ((flowState === null || flowState === "unattested") && bankDelta !== null && anchorNav > 0 && bankDelta / anchorNav > FLOW_BADGE_FLOOR) {
    caveats.push({
      badge: "cash came in",
      detail:
        "Off-broker bank cash is higher than at the strike, which market movement cannot " +
        "cause. That points to a capital inflow inside this period, and any inflow sits in " +
        "this figure as though it were performance.",
    });
  }

  if ((flowState === null || flowState === "unattested") && !paired) {
    caveats.push({
      badge: "broker leg only",
      detail:
        "The bank balance at the strike was unavailable, so this is measured on the IBKR " +
        "book alone. Moving reserve cash into the brokerage account therefore reads as a " +
        "gain. Historically that basis ran about +1.4pp/month high over stabilised months " +
        "(Jan–Jul 2026), and far worse in months with capital flows.",
    });
  }

  if (flowState === null && daily.change_may_include_flows) {
    caveats.push({
      badge: "incl. capital flows",
      detail:
        "The administrator's off-broker cash feed is not contemporaneous with the strike, so a capital " +
        "subscription or redemption in the period may sit in this figure as though it were performance. " +
        "Treat the magnitude as indicative only until the administrator's close lands.",
    });
  }

  if (recon?.adjusted) {
    caveats.push({
      badge: "mixed NAV basis",
      detail:
        "The current NAV was adjusted upward to match the sum of components, while the historical mark at " +
        "the strike was not. The two legs of this change are therefore on slightly different bases.",
    });
  }

  const brokerAsOf = admin?.broker_as_of ?? null;
  const brokerAsOfDate = parseNavDate(brokerAsOf);
  if (brokerAsOfDate && strike && daysBetween(brokerAsOfDate, strike) !== 0) {
    caveats.push({
      badge: "anchor predates strike",
      detail:
        `The broker mark subtracted here is from ${brokerAsOf}, not the ${strikeStr} strike — no broker NAV ` +
        "existed at the strike date. The period is wider than the label implies by that gap.",
    });
  }

  if (!daily.ibkr_live_as_of) {
    caveats.push({
      badge: "undated NAV",
      detail:
        "The live NAV carries no as-of date, so the fee accrual period is dated from the viewer's clock " +
        "rather than the data's own date.",
    });
  }

  if (reportedUsd !== null && !booked) {
    caveats.push({
      badge: "fees not yet booked",
      detail:
        `The administrator has posted ${reportedUsd === 0 ? "no" : "negligible"} expenses for this period ` +
        `(through ${actual?.through_date}), which reflects their monthly booking cycle rather than a ` +
        "fee-free period. A modelled management-fee accrual is used instead, so the net figure is not " +
        "overstated while the month is open.",
    });
  }

  if (actual?.unrecognised_expense_columns?.length) {
    caveats.push({
      badge: "unbooked expense line",
      detail:
        `The administrator is reporting expense columns this platform does not yet sum ` +
        `(${actual.unrecognised_expense_columns.join(", ")}), so the net figure understates costs ` +
        "by whatever they contain.",
    });
  }

  const correctedNet =
    isNum(recon?.calibration?.trailing_bias_pp) &&
    netPct !== null &&
    (flowState === "clean" || flowState === "adjusted")
      ? netPct - (recon!.calibration!.trailing_bias_pp as number) / 100
      : null;
  // The co-headline's base is the RAW net, NEVER the drift-corrected figure: the
  // trailing bias is measured against the official — which is net of accrued
  // carry — so on a profitable streak it arms mostly FROM the carry, and
  // haircutting a carry-corrected figure would subtract the incentive TWICE
  // (adversarial review 2026-09-02, P1). In that scenario the two figures
  // converge on their own (corrected ≈ raw × 0.8); whether the bias should
  // instead consume the post-carry residual is the open CIO decision flagged in
  // backend strategy/nav_flows.py — do not resolve it here.
  const estOfficialBase =
    (flowState === "clean" || flowState === "adjusted") && netPct !== null ? netPct : null;

  return {
    available: true,
    periodLabel,
    isMonthToDate: monthToDate,
    anchorLabel,
    grossPct,
    grossUsd,
    netPct,
    netUsd,
    mgmtFeeAccrualUsd,
    accrualDays,
    feeBasis,
    actualExpensesUsd: actualUsd,
    expensesThroughDate: actual?.through_date ?? null,
    caveats,
    flowState,
    percentSuppressed,
    suppressionReason,
    flowAdjustmentUsd:
      flowState !== null && isNum(daily.flow_adjustment_usd) ? daily.flow_adjustment_usd : null,
    flowSource: flowState !== null ? daily.flow_adjustment_source ?? null : null,
    dollarsFlowAdjusted: flowState !== null && isNum(daily.change_flow_adj_usd),
    estOfficialNetPct:
      estOfficialBase !== null
        ? estOfficialBase > 0
          ? estOfficialBase * (1 - INCENTIVE_RATE_MODEL)
          : estOfficialBase
        : null,
    estOfficialHaircutApplied: estOfficialBase !== null && estOfficialBase > 0,
    calibration: recon?.calibration ?? null,
    correctedNetPct: correctedNet,
    driftBiasPp: isNum(recon?.calibration?.trailing_bias_pp)
      ? (recon!.calibration!.trailing_bias_pp as number)
      : null,
    estNavpsNow: isNum(daily.est_navps_now) ? daily.est_navps_now : null,
    navpsAtStrike: isNum(daily.navps_at_strike) ? daily.navps_at_strike : null,
    estNavpsRetPct: isNum(daily.est_navps_ret_pct) ? daily.est_navps_ret_pct : null,
    feeAddBackUsd: isNum(daily.fee_payments_added_back_usd) ? daily.fee_payments_added_back_usd : null,
    official,
  };
}

/**
 * The change-% a NON-tile surface should print for "since the strike", flow-aware.
 * Mirrors buildPeriodReturnView's basis ladder (flow-adjusted → paired → broker-only)
 * so a page subtext can never disagree with the tile. Returns null when the flow
 * ledger says the percentage must be withheld — callers must then print a withheld
 * label, never 0 and never the raw broker figure.
 */
export function flowAwareChangePct(daily?: NavDaily | null): number | null {
  if (!daily) return null;
  if (daily.flow_state === "suppressed") return null;
  if (
    (daily.flow_state === "clean" || daily.flow_state === "adjusted") &&
    isNum(daily.change_flow_adj_pct)
  ) {
    return daily.change_flow_adj_pct;
  }
  if (isNum(daily.change_incl_bank_pct)) return daily.change_incl_bank_pct;
  return isNum(daily.change_since_official_pct) ? daily.change_since_official_pct : null;
}

/** One-line description of what the net figure is net OF, for the tile face. */
export function feeBasisLabel(v: PeriodReturnView): string {
  if (v.feeBasis === "actual") {
    return `net of actual administrator expenses through ${v.expensesThroughDate}`;
  }
  if (v.feeBasis === "actual+modelled_tail") {
    return `net of actual administrator expenses through ${v.expensesThroughDate} + ${v.accrualDays}d modelled`;
  }
  return `net of modelled ${(MGMT_FEE_ANNUAL_RATE * 100).toFixed(0)}%/yr mgmt accrual over ${v.accrualDays}d`;
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Canonical compliance framing for any surface showing this figure.
 *
 * Mirrors the wording already returned by the backend at
 * HelionBackend/risk_intraday_pnl/__init__.py:16-20 ("sole official source"), which is
 * the phrasing docs/HANDOFF_PERF_RECON_MTD_TILE.md section 5 asks new surfaces to match.
 * Defined once here so the three pages cannot drift apart on compliance copy.
 *
 * DRAFT — pending CIO / compliance sign-off before this is visible to anyone outside
 * the CIO.
 */
export const PERIOD_RETURN_FRAMING_BASE =
  "Estimated and unofficial. NAV Fund Services remains the sole official source of fund performance. " +
  "Fund-level NAV change, not an investor return.";

/** Methodology sentence when the flow ledger is ACTIVE (flowState non-null). */
export const PERIOD_RETURN_FRAMING_FLOW =
  "Measured as the IBKR book's move since the official strike, less capital flows attested in the " +
  "operator flow ledger. An unconfirmed month shows the unadjusted percentage with a 'flows " +
  "unconfirmed' flag; the percentage is withheld only when attested flows are too large for the " +
  "beginning-NAV base. Measured accuracy vs the official Net ROR: typically within ±0.8pp (mean abs), " +
  "worst observed +2.1pp, over stabilised months Jan–Jul 2026.";

/** Methodology sentence for the LEGACY fallback (backend without the flow ledger, or the
 *  flow fields absent) — the figure rendered then is the paired/broker basis with NO flow
 *  subtraction, and the copy must not claim otherwise. */
export const PERIOD_RETURN_FRAMING_LEGACY =
  "Measured on the IBKR book paired with off-broker bank cash (no flow ledger available from this " +
  "backend), so moving reserve cash between them cancels — but a subscription that arrives and is " +
  "deployed within the period is indistinguishable from performance until the next monthly strike.";

/** The framing for a built view — picks the sentence that matches the basis actually
 *  rendered, so static copy can never describe a methodology the figure does not use. */
export function periodReturnFraming(v: PeriodReturnView): string {
  return `${PERIOD_RETURN_FRAMING_BASE} ${v.flowState !== null ? PERIOD_RETURN_FRAMING_FLOW : PERIOD_RETURN_FRAMING_LEGACY}`;
}

export const PERIOD_RETURN_FEE_NOTE =
  "The net figure subtracts the administrator's ACTUAL booked expenses (management fee, administration, " +
  "professional, organization) where they are available; their accounting date runs a few business days " +
  `behind, so any uncovered tail is modelled at ${(MGMT_FEE_ANNUAL_RATE * 100).toFixed(0)}%/yr straight-line, ` +
  `actual/${MGMT_FEE_DAY_BASIS}, on the NAV at the strike. The headline is BEFORE the incentive fee ` +
  "(per-investor high-water marks make the exact figure non-computable at fund level) — but note the " +
  "OFFICIAL Net ROR IS net of the administrator's monthly ACCRUED incentive reallocation (verified vs " +
  "the Aug-2026 package), which is why the official prints ~20-23% of profit below this figure in up " +
  "months; the calibration line shows that measured wedge, and the 'est. official net · modelled' " +
  "co-headline applies a flat 20% model to the RAW net figure in profitable months and passes a " +
  "losing month through unchanged (no incentive accrues on a loss).";
